import { Fragment } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  EntityContentLayoutBlueprint,
  type EntityContentLayoutProps,
} from '@backstage/plugin-catalog-react/alpha';
import {
  EntitySwitch,
  isOrphan,
  EntityOrphanWarning,
  hasRelationWarnings,
  EntityRelationWarning,
  hasCatalogProcessingErrors,
  EntityProcessingErrorsPanel,
} from '@backstage/plugin-catalog';
import { EmptyContentState } from './EmptyContentState';

const useStyles = makeStyles<Theme, { infoCards: boolean }>(theme => ({
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: theme.spacing(2.5),

    /*
     * Card header actions — the refresh and edit-metadata buttons Backstage puts
     * on the About and Group profile cards.
     *
     * They are MUI `IconButton`s at their 48px default around a 24px glyph, in a
     * header whose own padding is 14px. The button, not the title, then sets the
     * header height: 77px against the 48px every other card header measures. The
     * mock draws this action as a bare 13px pencil at opacity 0.5, so the glyph
     * goes to 13px and the button to a 24px hit target (the WCAG 2.5.8 minimum;
     * the mock's bare 13px icon would be under it). The -2px block margin lets
     * the 24px button sit in the 20px content box without reopening the header.
     *
     * Scoped to the overview grid rather than written globally: these two are
     * the only card headers in the portal carrying icon buttons, and the sizing
     * is part of this page's card grammar.
     */
    '& [class*="bui-CardHeader"] [class*="MuiIconButton-root"]': {
      width: 24,
      height: 24,
      padding: 0,
      margin: '-2px 0',
      borderRadius: 6,
      color: 'var(--bui-fg-secondary)',
    },
    '& [class*="bui-CardHeader"] [class*="MuiIconButton-root"]:hover': {
      background: 'var(--hone-portal-fill-row-hover)',
      color: 'var(--bui-fg-primary)',
    },
    '& [class*="bui-CardHeader"] [class*="MuiIconButton-root"] [class*="MuiSvgIcon-root"]':
      {
        fontSize: 13,
      },

    /*
     * Case. Nothing in the mock is uppercased — zero occurrences across every
     * surface — and its eyebrow is 11/600 at 0.03em tracking in sentence case.
     * Two vendor components uppercase through CSS inside the overview grid (the
     * DOM text is already sentence case in both, so this is purely the
     * `text-transform` coming off):
     *
     *   - any `HeaderIconLinkRow` label, via the named
     *     `BackstageIconLinkVertical-label` class;
     *   - vendor field labels rendered as `Typography variant="h2"` inside a
     *     card body, which is how `AboutField` draws them. Matched on the
     *     variant rather than the component's own class, because that class
     *     comes from an UNNAMED `makeStyles` and arrives as a bare
     *     `makeStyles-label-<n>` any component could also produce.
     *
     * The entity eyebrow above the title ("REPOSITORY") is the same defect but
     * is NOT reachable from here — `BackstageHeader` renders outside the tab
     * panel. It needs `text-transform: none !important` on
     * `[class*='BackstageHeader-type']` in the theme.
     */
    /*
     * "See more" links move from the card's foot to the top-right of its
     * header, opposite the title — the mock's placement ("CI/CD ›",
     * "View definition ›").
     *
     * `InfoCard` renders its `deepLink` as a `BottomLink`: a divider and an
     * arrow link appended after the content. There is no header slot to move it
     * into, so the CARD becomes a two-column grid and the link is placed in the
     * header's row instead — header left, link right, content spanning both
     * beneath. The link cell repeats the header's bottom rule so the hairline
     * runs unbroken across the card.
     */
    '& [class*="MuiCard-root"]:has(> [class*="MuiBox-root"] [class*="BackstageBottomLink-root"])':
      {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
      },
    '& [class*="MuiCard-root"] > [class*="MuiCardHeader-root"]': {
      gridColumn: 1,
      gridRow: 1,
    },
    '& [class*="MuiCard-root"] > [class*="MuiBox-root"]:has([class*="BackstageBottomLink-root"])':
      {
        gridColumn: 2,
        gridRow: 1,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--hone-portal-hairline-soft)',
      },
    '& [class*="MuiCard-root"] > [class*="MuiCardContent-root"]': {
      gridColumn: '1 / -1',
      gridRow: 2,
    },
    /* The BottomLink's own divider was the rule between content and foot. */
    '& [class*="MuiBox-root"]:has(> [class*="BackstageBottomLink-root"]) > [class*="MuiDivider-root"]':
      {
        display: 'none',
      },
    '& [class*="BackstageBottomLink-root"]': {
      padding: '0 22px 0 0 !important',
      gap: 4,
    },
    '& [class*="BackstageBottomLink-boxTitle"]': {
      margin: '0 !important',
    },
    '& [class*="BackstageBottomLink-boxTitle"] strong': {
      fontSize: 12.5,
      fontWeight: 500,
    },
    /* The mock's affordance is a chevron, not MUI's arrow glyph. */
    '& [class*="BackstageBottomLink-arrow"]': {
      display: 'none',
    },
    '& [class*="BackstageBottomLink-root"]::after': {
      content: '"\\203A"',
      fontSize: 13,
      lineHeight: 1,
    },

    '& [class*="BackstageIconLinkVertical-label"]': {
      textTransform: 'none',
      letterSpacing: 0,
    },
    '& [class*="bui-CardBody"] [class*="MuiTypography-h2"]': {
      textTransform: 'none',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.03em',
    },
  },
  warningArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    '&:empty': { marginBottom: 0, display: 'none' },
  },
  mainContent: {
    display: 'flex',
    flexFlow: 'column',
    gap: theme.spacing(2.5),
    alignItems: 'stretch',
    minWidth: 0,
  },
  infoArea: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'stretch',
    gap: theme.spacing(2.5),
    minWidth: 0,
    '& > *': { flexShrink: 0, flexGrow: 0 },
  },
  contentArea: {
    display: 'flex',
    flexFlow: 'column',
    gap: theme.spacing(2.5),
    alignItems: 'stretch',
    minWidth: 0,
  },
  [theme.breakpoints.up('md')]: {
    root: {
      display: 'grid',
      gap: theme.spacing(2.5),
      gridTemplateAreas: `
        "content info"
        "content info"
      `,
      gridTemplateColumns: ({ infoCards }) => (infoCards ? '2fr 1fr' : '1fr'),
      alignItems: 'start',
    },
    mainContent: { display: 'contents' },
    contentArea: { gridArea: 'content' },
    /*
     * "2fr main + 1fr sticky rail", per the entity-page surface in the mock,
     * whose own grid is `2fr 1fr` on a 20px gap with `align-items: start` and
     * two plain flex columns — no `overflow` and no height cap on either.
     *
     * That absence is the point. The rail must NOT become a scroll container:
     * `overflow`, even `auto`, clips the card elevation, which leads with
     * `0 0 0 1px` — a hairline that sits OUTSIDE the border box — so every card
     * reads as shorn at the top and edges (and setting `overflow-y` forces
     * `overflow-x` to compute away from `visible`, so it clips on all four
     * sides). Pairing it with a `maxHeight` also gives the page a second
     * scrollbar. Sticky alone is what the mock asks for.
     */
    infoArea: {
      gridArea: 'info',
      position: 'sticky',
      top: theme.spacing(2.5),
      alignSelf: 'start',
      alignItems: 'stretch',
    },
  },
}));

type CardWithNode = {
  element: { props?: { node?: { spec?: { id?: string } } } };
};
const cardExtensionId = (card: unknown) =>
  (card as CardWithNode).element?.props?.node?.spec?.id;

function orderGroupCards<T>(contentCards: T[]): T[] {
  const ordered = [...contentCards];
  const ownershipIndex = ordered.findIndex(
    c => cardExtensionId(c) === 'entity-card:org/ownership',
  );
  const membersIndex = ordered.findIndex(
    c => cardExtensionId(c) === 'entity-card:org/members-list',
  );
  if (membersIndex !== -1 && ownershipIndex > membersIndex) {
    const [ownershipCard] = ordered.splice(ownershipIndex, 1);
    ordered.splice(membersIndex, 0, ownershipCard);
  }
  return ordered;
}

function moveCardAfter<T>(cards: T[], moveId: string, anchorId: string): T[] {
  const ordered = [...cards];
  const anchorIndex = ordered.findIndex(c => cardExtensionId(c) === anchorId);
  const moveIndex = ordered.findIndex(c => cardExtensionId(c) === moveId);
  if (anchorIndex === -1 || moveIndex === -1 || moveIndex === anchorIndex + 1) {
    return ordered;
  }
  const [card] = ordered.splice(moveIndex, 1);
  const insertAt = ordered.findIndex(c => cardExtensionId(c) === anchorId);
  ordered.splice(insertAt + 1, 0, card);
  return ordered;
}

function orderInfoCards<T>(infoCards: T[]): T[] {
  const ordered = [...infoCards];
  const aboutIndex = ordered.findIndex(
    c => cardExtensionId(c) === 'entity-card:catalog/about',
  );
  const ratingsIndex = ordered.findIndex(
    c => cardExtensionId(c) === 'entity-card:entity-feedback/ratings-buttons',
  );
  if (aboutIndex !== -1 && ratingsIndex !== -1 && ratingsIndex < aboutIndex) {
    const [ratingsCard] = ordered.splice(ratingsIndex, 1);
    const insertAt = ordered.findIndex(
      c => cardExtensionId(c) === 'entity-card:catalog/about',
    );
    ordered.splice(insertAt + 1, 0, ratingsCard);
  }
  // Ownership reads directly under About, per the mock's rail order, with the
  // PHI scope card beneath it.
  const withOwnership = moveCardAfter(
    ordered,
    'entity-card:catalog/entity-ownership',
    'entity-card:catalog/about',
  );
  const withPhi = moveCardAfter(
    withOwnership,
    'entity-card:catalog/phi-scope',
    'entity-card:catalog/entity-ownership',
  );
  return moveCardAfter(
    withPhi,
    'entity-card:catalog/links',
    'entity-card:org/group-profile',
  );
}

function OverviewLayout({ cards }: EntityContentLayoutProps) {
  const infoCards = orderInfoCards(cards.filter(card => card.type === 'info'));
  const contentCards = orderGroupCards(
    cards.filter(card => !card.type || card.type === 'content'),
  );
  const classes = useStyles({ infoCards: infoCards.length > 0 });

  return (
    <>
      <div className={classes.warningArea}>
        <EntitySwitch>
          <EntitySwitch.Case if={isOrphan}>
            <EntityOrphanWarning />
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasRelationWarnings}>
            <EntityRelationWarning />
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={hasCatalogProcessingErrors}>
            <EntityProcessingErrorsPanel />
          </EntitySwitch.Case>
        </EntitySwitch>
      </div>
      <div className={classes.root}>
        {infoCards.length > 0 && (
          <div className={classes.infoArea}>
            {infoCards.map((card, index) => (
              <Fragment key={index}>{card.element}</Fragment>
            ))}
          </div>
        )}
        <div className={classes.mainContent}>
          <div className={classes.contentArea}>
            {contentCards.length > 0 ? (
              contentCards.map((card, index) => (
                <Fragment key={index}>{card.element}</Fragment>
              ))
            ) : (
              <EmptyContentState />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const overviewLayout = EntityContentLayoutBlueprint.make({
  name: 'relations-bottom',
  params: {
    loader: async () => OverviewLayout,
  },
});

export const overviewLayoutModule = createFrontendModule({
  pluginId: 'app',
  extensions: [overviewLayout],
});
